/**
 * Expands a blocked date range into multiple occurrences based on recurrence rules.
 * Used for "Repeat" in Blocked dates form (weekly on selected days).
 */

type RepeatType = 'none' | 'weekly';

export const WEEKDAYS = [1, 2, 3, 4, 5] as const; // Mon–Fri
export const WEEKENDS = [0, 6] as const; // Sun, Sat

const MAX_WEEKLY_OCCURRENCES = 52;

export interface RecurrenceOptions {
  repeatType: RepeatType;
  /** For weekly: which weekdays (0–6, Sun–Sat). */
  weeklyDays: number[];
  /** End after this many occurrences (used when endCondition === 'after_count'). */
  endAfterCount?: number;
  /** End when we reach or pass this date (used when endCondition === 'until_date'). */
  repeatUntilDate?: Date | null;
  /** Which end condition is active. */
  endCondition: 'until_date' | 'after_count';
}

interface BlockOccurrence {
  start: Date;
  end: Date;
}

/** Optional start/end time-of-day per weekday (0–6). Only time part of start/end is used. */
export type DailyTimeRanges = Partial<Record<number, { start: Date; end: Date }>>;

/**
 * Returns the number of occurrences that would be generated (for preview),
 * or null if invalid / no recurrence.
 */
export function countRecurrenceOccurrences(
  firstStart: Date,
  firstEnd: Date,
  blockAllDay: boolean,
  options: RecurrenceOptions,
  dailyTimeRanges?: DailyTimeRanges
): number | null {
  const occurrences = expandRecurrence(firstStart, firstEnd, blockAllDay, options, dailyTimeRanges);
  return occurrences?.length ?? null;
}

/**
 * Expands recurrence into a list of { start, end } for each occurrence.
 * - repeatType 'none': returns single occurrence [firstStart, firstEnd].
 * - 'weekly': same time range on each date that is in weeklyDays, until end condition.
 * - If dailyTimeRanges[dayOfWeek] is set, that day uses that time-of-day; otherwise full day (00:00–23:59).
 */
export function expandRecurrence(
  firstStart: Date,
  firstEnd: Date,
  blockAllDay: boolean,
  options: RecurrenceOptions,
  dailyTimeRanges?: DailyTimeRanges
): BlockOccurrence[] {
  const { repeatType, weeklyDays, endAfterCount, repeatUntilDate, endCondition } = options;

  const maxCount =
    endCondition === 'after_count' && endAfterCount != null
      ? Math.min(endAfterCount, MAX_WEEKLY_OCCURRENCES)
      : MAX_WEEKLY_OCCURRENCES;

  if (repeatType === 'none') {
    return [{ start: new Date(firstStart), end: new Date(firstEnd) }];
  }

  if (weeklyDays.length === 0) {
    return [];
  }

  const occurrences: BlockOccurrence[] = [];
  const normalizeToDate = (d: Date) => {
    const out = new Date(d);
    out.setHours(0, 0, 0, 0);
    return out;
  };
  const firstStartNorm = normalizeToDate(firstStart);
  const repeatUntilNorm = repeatUntilDate ? normalizeToDate(repeatUntilDate) : null;

  const getOccurrenceForDate = (occurrenceDate: Date, dayOfWeek: number): BlockOccurrence => {
    const start = new Date(occurrenceDate);
    const end = new Date(occurrenceDate);
    const dayTimes = dailyTimeRanges?.[dayOfWeek];
    if (dayTimes) {
      start.setHours(dayTimes.start.getHours(), dayTimes.start.getMinutes(), dayTimes.start.getSeconds(), dayTimes.start.getMilliseconds());
      end.setHours(dayTimes.end.getHours(), dayTimes.end.getMinutes(), dayTimes.end.getSeconds(), dayTimes.end.getMilliseconds());
      if (end <= start) {
        end.setDate(end.getDate() + 1);
      }
    } else if (blockAllDay) {
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    } else {
      start.setHours(firstStart.getHours(), firstStart.getMinutes(), firstStart.getSeconds(), firstStart.getMilliseconds());
      end.setHours(firstEnd.getHours(), firstEnd.getMinutes(), firstEnd.getSeconds(), firstEnd.getMilliseconds());
      if (end <= start) {
        end.setDate(end.getDate() + 1);
      }
    }
    return { start, end };
  };

  let current = new Date(firstStartNorm);
  const oneDayMs = 24 * 60 * 60 * 1000;
  while (occurrences.length < maxCount) {
    if (repeatUntilNorm && current > repeatUntilNorm) break;
    const dayOfWeek = current.getDay();
    if (weeklyDays.includes(dayOfWeek)) {
      occurrences.push(getOccurrenceForDate(new Date(current), dayOfWeek));
    }
    current.setTime(current.getTime() + oneDayMs);
  }
  return occurrences;
}
